import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import {
  CHAT_CHANNEL_IDS,
  CHAT_TEXT_LIMIT,
} from "@frontier/protocol/chat-contracts";
import type { ChatChannelId } from "@frontier/protocol/chat-contracts";
import type { ChatSnapshot } from "./chat-session";
import "./chat.css";

export const CHAT_LABELS: Record<ChatChannelId, string> = {
  global: "Общий",
  faction: "Фракция",
  location: "Локация",
  party: "Группа",
};
const UNAVAILABLE: Record<ChatChannelId, string> = {
  global: "Общий канал пока недоступен.",
  faction: "Канал откроется после выбора фракции.",
  location: "Подключаем канал области…",
  party: "Вступи в группу, чтобы общаться с её участниками.",
};
export interface ChatPanelProps {
  chat: ChatSnapshot;
  onSelect: (channel: ChatChannelId) => void;
  onDraft: (channel: ChatChannelId, text: string) => void;
  onSend: () => boolean;
  onReading: (channel: ChatChannelId | null) => void;
  onClose: () => void;
}
export function chatEnterSends(event: {
  key: string;
  shiftKey: boolean;
  isComposing: boolean;
}): boolean {
  return event.key === "Enter" && !event.shiftKey && !event.isComposing;
}
export function ChatPanel(props: ChatPanelProps) {
  const { chat } = props;
  const channel = chat.channels[chat.selected];
  const enabled =
    chat.connected && chat.contextId !== null && channel.scope !== null;
  const panel = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const composing = useRef(false);
  const close = useRef(props.onClose);
  close.current = props.onClose;
  const [atBottom, setAtBottom] = useState(true);
  const bottomAnchor = useRef(true);
  const [visible, setVisible] = useState(
    () =>
      typeof document === "undefined" ||
      (!document.hidden && document.hasFocus()),
  );
  useLayoutEffect(() => {
    const previous = document.activeElement;
    const node = panel.current;
    panel.current?.focus();
    if (matchMedia("(pointer: fine)").matches) input.current?.focus();
    const visibility = () =>
      setVisible(!document.hidden && document.hasFocus());
    window.addEventListener("focus", visibility);
    window.addEventListener("blur", visibility);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.removeEventListener("focus", visibility);
      window.removeEventListener("blur", visibility);
      document.removeEventListener("visibilitychange", visibility);
      if (node?.contains(document.activeElement) && previous instanceof HTMLElement && previous.isConnected)
        previous.focus();
    };
  }, []);
  useEffect(() => {
    bottomAnchor.current = true;
    setAtBottom(true);
  }, [chat.selected]);
  useEffect(() => {
    const node = list.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      if (bottomAnchor.current) node.scrollTop = node.scrollHeight;
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    props.onReading(visible && atBottom ? chat.selected : null);
    return () => props.onReading(null);
  }, [props.onReading, visible, atBottom, chat.selected, chat.contextId]);
  useEffect(() => {
    if (atBottom && list.current)
      list.current.scrollTop = list.current.scrollHeight;
  }, [channel.messages, atBottom]);
  const keys = (event: KeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
    if (
      event.target === input.current &&
      chatEnterSends({
        key: event.key,
        shiftKey: event.shiftKey,
        isComposing: composing.current || event.nativeEvent.isComposing,
      })
    ) {
      event.preventDefault();
      props.onSend();
    }
    if (event.key === "Escape" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      close.current();
    }
  };
  const empty = !chat.connected
    ? "Связь с чатом прервана."
    : !chat.contextId
      ? "Подключаем каналы…"
      : !channel.scope
        ? UNAVAILABLE[chat.selected]
        : "Сообщений пока нет.";
  return (
      <section
        className="chat-panel"
        ref={panel}
        tabIndex={-1}
        role="region"
        aria-labelledby="chat-title"
        onKeyDownCapture={keys}
      >
        <header className="chat-heading">
          <div>
            <h2 id="chat-title">ЧАТ</h2>
            <span>{channel.scope?.label ?? CHAT_LABELS[chat.selected]}</span>
          </div>
          <button
            type="button"
            aria-label="Закрыть чат"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>
        <nav className="chat-tabs" aria-label="Каналы чата" role="tablist">
          {CHAT_CHANNEL_IDS.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={id === chat.selected}
              aria-controls="chat-feed"
              id={`chat-tab-${id}`}
              onClick={() => props.onSelect(id)}
            >
              {CHAT_LABELS[id]}
              {chat.channels[id].unread > 0 && (
                <i>{chat.channels[id].unread}</i>
              )}
            </button>
          ))}
        </nav>
        <div
          className="chat-feed"
          id="chat-feed"
          ref={list}
          role="log"
          aria-live="polite"
          aria-label={`Сообщения: ${CHAT_LABELS[chat.selected]}`}
          onScroll={() => {
            const node = list.current;
            if (node) {
              const bottom =
                node.scrollHeight - node.clientHeight - node.scrollTop <= 16;
              bottomAnchor.current = bottom;
              setAtBottom(bottom);
            }
          }}
        >
          {channel.messages.length === 0 && (
            <p className="chat-empty">{empty}</p>
          )}
          {channel.messages.map((message) => (
            <p
              className="chat-message"
              key={message.id}
              data-message-id={message.id}
            >
              <b>{message.senderName}</b>
              <span>{message.text}</span>
            </p>
          ))}
        </div>
        {!atBottom && (
          <button
            className="chat-latest"
            onClick={() => {
              panel.current?.focus({ preventScroll: true });
              bottomAnchor.current = true;
              setAtBottom(true);
            }}
          >
            К новым сообщениям ↓
          </button>
        )}
        <div className="chat-compose">
          {(channel.error || !enabled) && (
            <p className="chat-error" role="status">
              {channel.error || empty}
            </p>
          )}
          <div className="chat-compose-row">
            <input
              ref={input}
              aria-label="Сообщение чата"
              type="text"
              enterKeyHint="send"
              autoComplete="off"
              maxLength={CHAT_TEXT_LIMIT}
              placeholder={enabled ? "Написать сообщение…" : "Канал недоступен"}
              disabled={!enabled}
              value={channel.draft}
              onChange={(event) =>
                props.onDraft(chat.selected, event.target.value)
              }
              onCompositionStart={() => {
                composing.current = true;
              }}
              onCompositionEnd={() => {
                composing.current = false;
              }}
            />
            <button
              type="button"
              className="chat-send"
              aria-label="Отправить сообщение"
              disabled={!enabled || channel.pending || !channel.draft.trim()}
              onClick={() => props.onSend()}
            >
              {channel.pending ? "…" : "↑"}
            </button>
          </div>
          <small>
            История не сохраняется · {channel.draft.length}/
            {CHAT_TEXT_LIMIT}
          </small>
        </div>
      </section>
  );
}
